import { useState } from 'react'

export interface AuditEntry {
  /** ISO 8601 datetime string (UTC, with Z suffix). */
  timestamp: string
  action: string
  type: 'user' | 'auto'
}

interface AuditTrailProps {
  entries: AuditEntry[]
}

function formatTime(iso: string): string {
  return new Date(iso).toTimeString().slice(0, 5)
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

function formatDateHeader(date: Date): string {
  const today = startOfDay(new Date())
  const target = startOfDay(date)
  const diffDays = Math.round((today - target) / 86_400_000)
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

interface DateGroup {
  key: number
  label: string
  entries: AuditEntry[]
}

function groupByDate(entries: AuditEntry[]): DateGroup[] {
  const byKey = new Map<number, { date: Date; entries: AuditEntry[] }>()
  for (const entry of entries) {
    const d = new Date(entry.timestamp)
    const key = startOfDay(d)
    const group = byKey.get(key)
    if (group) {
      group.entries.push(entry)
    } else {
      byKey.set(key, { date: d, entries: [entry] })
    }
  }
  return Array.from(byKey.entries())
    .map(([key, { date, entries }]) => ({
      key,
      label: formatDateHeader(date),
      // Newest first within the day.
      entries: [...entries].sort((a, b) => b.timestamp.localeCompare(a.timestamp)),
    }))
    .sort((a, b) => b.key - a.key)
}

export function AuditTrail({ entries }: AuditTrailProps) {
  const [isOpen, setIsOpen] = useState(false)
  const isEmpty = entries.length === 0
  const groups = isOpen && !isEmpty ? groupByDate(entries) : []

  return (
    <div>
      <button
        type="button"
        className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer flex items-center gap-1.5 py-1.5 w-full hover:text-foreground transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`text-[10px] transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>
          ›
        </span>
        Audit trail ({entries.length})
      </button>

      {isOpen && (
        <div>
          {isEmpty ? (
            <p className="text-[10px] text-muted-foreground italic py-1.5">
              No actions logged yet.
            </p>
          ) : (
            groups.map(group => (
              <div key={group.key}>
                <div className="text-[10px] font-semibold text-foreground/70 pt-2 pb-1">
                  {group.label}
                </div>
                {group.entries.map((entry, i) => (
                  <div
                    key={`${entry.timestamp}-${i}`}
                    className={`text-[10px] text-muted-foreground leading-relaxed py-1.5 flex gap-2 ${
                      i < group.entries.length - 1 ? 'border-b border-border/40' : ''
                    }`}
                  >
                    <span className="min-w-[42px] opacity-70 shrink-0">{formatTime(entry.timestamp)}</span>
                    <span className="flex-1">{entry.action}</span>
                    {entry.type === 'auto' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 self-start">
                        auto
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
